import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { MenuItem, Select, FormControl, InputLabel, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import axios from "axios";
import * as XLSX from 'xlsx';
import { useSnackbar } from "notistack";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ResizableDataGrid from "components/ResizableDataGrid";

function RecentlySold() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMonth, setExportMonth] = useState(format(new Date(), "yyyy-MM"));

  // Generate a list of months for the filter (last 2 years)
  const generateMonths = () => {
    const months = [];
    const date = new Date();
    for (let i = 0; i < 24; i++) {
      months.push(format(date, "yyyy-MM"));
      date.setMonth(date.getMonth() - 1);
    }
    return months;
  };

  const months = generateMonths();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/product/last10sold`,
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );

      if (res.data.success) {
        // The API returns an object where numeric keys are items and 'success' is a boolean
        const data = res.data.data || res.data;
        const rawItems = Object.entries(data)
          .filter(([key, value]) => !isNaN(key) && typeof value === 'object')
          .map(([_, value]) => value);

        const mappedItems = rawItems.map(item => ({
          id: `${item.order_id}-${item.product_id}-${Math.random()}`,
          product_id: item.product_id,
          order_id: item.order_id,
          title: item.product_name || "N/A",
          catalogue_number: item.catalogue_ids || "N/A",
          billing_id: item.billing_id || "N/A",
          order_date: item.created_at,
          status: item.status,
        }));

        setItems(mappedItems);
      } else {
        enqueueSnackbar(res.data.message || "Failed to fetch data", { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error fetching sold products", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    let dataToExportRaw = items;
    if (exportMonth !== "All") {
      dataToExportRaw = items.filter(item => {
        const itemMonth = format(new Date(item.order_date), "yyyy-MM");
        return itemMonth === exportMonth;
      });
    }

    if (dataToExportRaw.length === 0) {
      enqueueSnackbar("No data to export for selected month", { variant: "info" });
      return;
    }

    const dataToExport = dataToExportRaw.map(item => ({
      "Product Title": item.title,
      "Catalogue Number": item.catalogue_number,
      "Order ID (Billing ID)": item.billing_id,
      "Order Date": format(new Date(item.order_date), "dd MMM yyyy, h:mm a"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recently Sold Products");

    const fileName = `Recently_Sold_Products_${exportMonth === "All" ? "AllTime" : exportMonth}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setExportDialogOpen(false);
    enqueueSnackbar("Excel exported successfully", { variant: "success" });
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 250,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <MDTypography
          variant="button"
          fontWeight="medium"
          color="info"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => window.open(`/products/${params.row.product_id}`, "_blank")}
        >
          {params.value}
        </MDTypography>
      ),
    },
    {
      field: "catalogue_number",
      headerName: "Catalog ID",
      width: 250,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "billing_id",
      headerName: "Billing ID",
      width: 250,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const isStoreSale = !params.value || params.value === "N/A" || params.value === null;
        const displayText = isStoreSale ? "Store Sales" : params.value;

        if (isStoreSale) {
          return (
            <MDTypography variant="button" fontWeight="medium" color="text">
              {displayText}
            </MDTypography>
          );
        }

        return (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="info"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => window.open(`/orders/${params.row.order_id}`, "_blank")}
          >
            {displayText}
          </MDTypography>
        );
      },
    },
    {
      field: "order_date",
      headerName: "Order Date",
      width: 250,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => {
        if (!params.value) return "";
        const timeZone = "UTC";
        const zonedDate = toZonedTime(new Date(params.value), timeZone);
        return format(zonedDate, "dd MMM yyyy, h:mm a");
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Recently Sold Products
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  onClick={() => setExportDialogOpen(true)}
                  size="small"
                >
                  Export to Excel
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <MDBox px={2} pb={2} sx={{ 
                    height: 600,
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    scrollbarWidth: "none",
                    "-ms-overflow-style": "none",
                    "& .MuiDataGrid-virtualScroller": {
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                      "-ms-overflow-style": "none",
                    },
                    "& .MuiDataGrid-root": {
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                      "-ms-overflow-style": "none",
                    }
                  }}>
                    <ResizableDataGrid
                      rows={items}
                      columns={columns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 100,
                          },
                        },
                      }}
                      pageSizeOptions={[10, 25, 50, 100]}
                      disableRowSelectionOnClick
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Export Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            minWidth: "400px"
          }
        }}
      >
        <MDBox p={2} textAlign="center">
          <MDTypography variant="h5" fontWeight="medium" color="info">
            Export Sold Products
          </MDTypography>
        </MDBox>
        <DialogContent>
          <MDBox pt={1} pb={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="export-month-label">Select Month</InputLabel>
              <Select
                labelId="export-month-label"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                label="Select Month"
                sx={{ height: 45 }}
              >
                <MenuItem value="All">All Time</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {format(new Date(month + "-01"), "MMMM yyyy")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>
        </DialogContent>
        <MDBox display="flex" justifyContent="center" gap={2} pb={3} px={3}>
          <MDButton 
            onClick={() => setExportDialogOpen(false)} 
            variant="contained"
            sx={{ 
              backgroundColor: "#7b809a", 
              color: "#fff",
              "&:hover": { backgroundColor: "#6a6f8a" }
            }}
          >
            Cancel
          </MDButton>
          <MDButton 
            onClick={handleExport} 
            color="info" 
            variant="gradient"
          >
            Export
          </MDButton>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default RecentlySold;
